<template>
  <div class="ownership-editor">
    <div class="form-group">
      <div class="selector-button-label text-medium">Owned by</div>
      <single-select
        :options="users"
        :value="owner"
        :auto-focus="autoFocusOwnerField"
        @select="setOwner($event)"
        placeholder="Choose an owner"
      >
        <template v-slot:default="item">
          <div class="d-flex user-pill">
            <user-short class="pl-2" :user-id="item.value.id" length="18"></user-short>
            <span class="user-text text-truncate" :title="item.value.displayName">{{
              item.value.displayName
            }}</span>
          </div>
        </template>
        <template v-slot:listItem="option">
          <div class="d-flex dropdown-item-container">
            <user-short class="pl-2" :user-id="option.option.id" length="18"></user-short>
            <span class="pl-2 user-text">{{ option.option.displayName }}</span>
          </div>
        </template>
        <template v-slot:noResultMessage>
          <div>
            {{ noResultMessage }}
            <br />
            <b-button class="px-0" variant="link" @click="showInviteModal = true">
              + Invite people
            </b-button>
          </div>
        </template>
      </single-select>
    </div>
    <div class="form-group">
      <div class="selector-button-label text-medium">Co-Owned by</div>
      <multi-select
        :options="selectableUsersList"
        :values="selectedCoOwners"
        @change="onCoOwnersChange($event)"
        placeholder="Choose co-owners"
      >
        <template v-slot:default="item">
          <div class="d-flex user-pill overflow-hidden">
            <user-short class="pl-2" :user-id="item.value.id" length="18"></user-short>
            <span class="user-text text-truncate" :title="item.value.displayName">{{
              item.value.displayName
            }}</span>
          </div>
        </template>
        <template v-slot:listItem="option">
          <div class="d-flex dropdown-item-container">
            <user-short class="pl-2" :user-id="option.option.id" length="18"></user-short>
            <span class="pl-2 user-text">{{ option.option.displayName }}</span>
          </div>
        </template>
        <template v-slot:noResultMessage>
          <div>
            {{ noResultMessage }}
            <br />
            <b-button class="px-0" variant="link" @click="showInviteModal = true">
              + Invite people
            </b-button>
          </div>
        </template>
      </multi-select>
    </div>
    <div class="pt-2">
      <submit-buttons :is-loading="isSubmitting" @save="$emit('save')" @cancel="$emit('cancel')" />
    </div>
    <manage-team-members
      v-if="!team.isOrgLike && showInviteModal"
      v-model="showInviteModal"
      :org-id="orgId"
      :team-id="teamId"
    />
    <org-invite-modal
      v-if="team.isOrgLike && showInviteModal"
      v-model="showInviteModal"
      :org-id="orgId"
    />
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import ManageTeamMembers from '@/components/ManageTeamMembers'
import OrgInviteModal from '@/components/OrgInviteModal'
import SubmitButtons from '@/components/SubmitButtons'
import EventBusMixin from '@/mixins/EventBusMixin'
import * as R from 'ramda'
import UserShort from './UserShort'
import MultiSelect from '@/components/form/MultiSelect'
import SingleSelect from '@/components/form/SingleSelect'

export default {
  name: 'OwnershipEditor',
  components: {
    UserShort,
    MultiSelect,
    SingleSelect,
    SubmitButtons,
    ManageTeamMembers,
    OrgInviteModal,
    BButton
  },
  mixins: [EventBusMixin],
  data() {
    return {
      showInviteModal: false,
      autoFocusOwnerField: true
    }
  },
  props: ['userIds', 'entity', 'isSubmitting', 'teamId', 'orgId'],
  computed: {
    users() {
      return this.userIds
        .map(userId => this.$store.getters.userById(userId))
        .filter(user => !!user)
        .sort((a, b) => {
          return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
        })
        .map(user => ({ ...user, label: user.displayName }))
    },
    selectableUsersList() {
      return R.differenceWith((x, y) => x.id === y.id, this.users, this.selectedCoOwners)
    },
    owner() {
      if (this.entity.ownerId) {
        return this.$store.getters.userById(this.entity.ownerId)
      } else {
        return null
      }
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    selectedCoOwners() {
      return this.entity.coOwnerIds
        .map(userId => this.$store.getters.userById(userId))
        .filter(user => !!user)
    },
    noResultMessage() {
      return `Make sure the person you're looking for is a part of this ${
        this.team.isOrgLike ? 'organization' : 'team'
      }`
    }
  },
  methods: {
    updateUsers() {
      this.userIds.forEach(userId => {
        this.$store.dispatch('getUser', { id: userId })
      })
    },
    setOwner(user) {
      let entity = this.entity
      if (user) {
        entity.ownerId = user.id
        if (entity.coOwnerIds.indexOf(user.id) > -1) {
          entity.coOwnerIds = entity.coOwnerIds.filter(coOwnerId => coOwnerId !== user.id)
        }
      } else {
        entity.ownerId = null
      }
    },
    isCoOwner(user) {
      return this.entity.coOwnerIds.indexOf(user.id) > -1
    },
    onCoOwnersChange(values) {
      this.entity.coOwnerIds = values.map(R.prop('id'))
      if (this.entity.coOwnerIds.indexOf(this.entity.ownerId) > -1) {
        this.entity.ownerId = null
      }
    }
  },
  mounted() {
    this.updateUsers()
    this.listenToEventBus('SUBMIT_FORM', () => {
      this.$emit('save')
    })
    this.autoFocusOwnerField = false
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/input';

/deep/ .user-short-container:focus {
  outline: none;
}

.none-item-text {
  padding-left: 24px;
}

.multi-user-popover {
  background: #f6f6f6;
  border-radius: 16px;
}

/deep/ .none-item .dropdown-item {
  padding-left: 20px;
}

.form-group {
  margin-bottom: 12px;
}

.user-text {
  top: 2px;
  font-size: 0.875rem;
  line-height: 18px;
}

/deep/ .dropdown-item {
  padding-top: 0;
  padding-bottom: 0;
}

/deep/ .dropdown-menu {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item-container {
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 0.875rem;
}

.user-pill {
  padding: 2px 0 1px;

  /deep/ .user-short-container {
    padding: 0 5px 0 4px !important;
  }
}
/deep/ .user-short-container {
  padding-top: 0 !important;
}
</style>
